import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, CustomerAtSale, CustomerTagBadge } from '../api';
import { customerEndpoints } from '../api';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Panel, formatWhen } from '../ui';
import { INPUT } from '../pages/event/shared';

/**
 * Notes and tags on a customer, wherever staff meet one (D-358).
 *
 * Three pieces, so the customer page, the order page, the orders list and the
 * New Order page all say the same thing the same way: a tag that warns is red
 * everywhere, and a label is gray everywhere. A do-not-sell that is loud on one
 * screen and a gray chip on another is the failure D-270 was written about.
 */

/** A row of tag badges. Warning tags first, in red. */
export function TagBadges({ tags }: { tags: CustomerTagBadge[] | undefined }) {
  if (!tags || tags.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge key={tag.code} tone={tag.warns_at_sale ? 'bad' : 'accent'}>
          {tag.name}
        </Badge>
      ))}
    </span>
  );
}

/**
 * What to know before selling: the warning, with the note that says why, and
 * the latest note. It never blocks anything; a person reads it and decides.
 */
export function CustomerWarnings({ record }: { record: CustomerAtSale | null | undefined }) {
  if (!record || (record.tags.length === 0 && record.notes_count === 0)) return null;
  return (
    <div className="space-y-2">
      {record.warnings.map((warning) => (
        <div key={warning.tag_code} role="alert" className="rounded-lg border border-bad/40 bg-bad-soft px-3 py-2 text-sm text-bad">
          <p className="font-semibold">Tagged “{warning.name}”. You can still go ahead; read this first.</p>
          <p className="mt-0.5">{warning.note ? `“${warning.note.body}” — ${warning.note.author_name ?? 'staff'}, ${formatWhen(warning.note.created_at)}` : 'Nobody wrote down why. Ask a manager before going ahead.'}</p>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-600">
        <TagBadges tags={record.tags} />
        {record.notes_count > 0 && record.latest_note ? (
          <span>
            {record.notes_count} note{record.notes_count === 1 ? '' : 's'}. Latest: “{record.latest_note.body}” — {record.latest_note.author_name ?? 'staff'}, {formatWhen(record.latest_note.created_at)}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** The file on one customer record: tags to put on and take off, and notes to add. */
export function CustomerRecordPanel({ customerKey }: { customerKey: string }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const record = useQuery({ queryKey: ['customers', 'record', customerKey], queryFn: () => customerEndpoints.record(customerKey) });
  const kinds = useQuery({ queryKey: ['customers', 'tag-kinds'], queryFn: () => customerEndpoints.tagKinds(), enabled: can('customers.tag') });
  const [note, setNote] = useState('');
  const [tagCode, setTagCode] = useState('');
  const [tagNote, setTagNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['customers', 'record', customerKey] });
  const onError = (cause: ApiError) => setError(cause.message);

  const addNote = useMutation({
    mutationFn: () => customerEndpoints.addNote(customerKey, note.trim()),
    onSuccess: () => {
      setNote('');
      setError(null);
      refresh();
    },
    onError,
  });
  const assign = useMutation({
    mutationFn: () => customerEndpoints.assignTag(customerKey, { tag_code: tagCode, note: tagNote.trim() || null }),
    onSuccess: () => {
      setTagCode('');
      setTagNote('');
      setError(null);
      refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: (input: { code: string; reason: string | null }) => customerEndpoints.removeTag(customerKey, input.code, input.reason),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError,
  });

  const data = record.data;
  const held = new Set((data?.tags ?? []).map((tag) => tag.code));
  const chosen = kinds.data?.tags.find((kind) => kind.code === tagCode);
  return (
    <Panel title="Notes and tags" description="For staff only. A note cannot be edited or deleted once written, so write what you would be content for a colleague to read in a year.">
      {error ? (
        <div className="px-5 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      <div className="space-y-3 border-b border-line px-5 py-4">
        {(data?.tags ?? []).length === 0 ? <p className="text-sm text-ink-400">No tags.</p> : null}
        <ul className="space-y-2">
          {(data?.tags ?? []).map((tag) => (
            <li key={tag.code} className="flex flex-wrap items-center gap-2 text-sm">
              <Badge tone={tag.warns_at_sale ? 'bad' : 'accent'}>{tag.name}</Badge>
              <span className="text-ink-600">
                by {tag.assigned_by_name ?? 'staff'}, {formatWhen(tag.assigned_at)}
                {tag.note ? ` — “${tag.note.body}”` : ''}
              </span>
              {can('customers.tag') ? (
                <button
                  type="button"
                  className="text-xs text-ink-400 underline"
                  onClick={() => {
                    const reason = window.prompt(`Why is ${tag.name} coming off? (optional, kept as a note)`);
                    if (reason === null) return;
                    remove.mutate({ code: tag.code, reason: reason.trim() || null });
                  }}
                >
                  Take off
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {can('customers.tag') ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (tagCode) assign.mutate();
            }}
          >
            <select value={tagCode} onChange={(e) => setTagCode(e.target.value)} className={INPUT} aria-label="Tag to add">
              <option value="">Add a tag…</option>
              {(kinds.data?.tags ?? [])
                .filter((kind) => !held.has(kind.code))
                .map((kind) => (
                  <option key={kind.code} value={kind.code}>
                    {kind.name}
                  </option>
                ))}
            </select>
            <input
              value={tagNote}
              onChange={(e) => setTagNote(e.target.value)}
              placeholder={chosen?.warns_at_sale ? 'Why? The cashier will see this' : 'Why? (optional)'}
              className={`${INPUT} w-full max-w-sm`}
              aria-label="Why this tag"
            />
            <Button type="submit" disabled={!tagCode || assign.isPending}>
              Add tag
            </Button>
          </form>
        ) : null}
      </div>

      <div className="space-y-3 px-5 py-4">
        {can('customers.note') ? (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (note.trim()) addNote.mutate();
            }}
          >
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} className={`${INPUT} w-full max-w-xl`} aria-label="New note" placeholder="Called about the wheelchair space…" />
            <Button type="submit" variant="primary" disabled={!note.trim() || addNote.isPending}>
              Add note
            </Button>
          </form>
        ) : null}
        {(data?.notes ?? []).length === 0 ? <p className="text-sm text-ink-400">{data ? 'No notes.' : 'Loading…'}</p> : null}
        <ul className="space-y-2">
          {(data?.notes ?? []).map((entry) => (
            <li key={entry.id} className="rounded-lg border border-line px-3 py-2 text-sm">
              <p className="whitespace-pre-wrap text-ink-900">{entry.body}</p>
              <p className="mt-1 text-xs text-ink-400">
                {entry.author_name ?? 'staff'} · {formatWhen(entry.created_at)}
                {entry.tag_name ? ` · with the ${entry.tag_name} tag` : ''}
              </p>
            </li>
          ))}
        </ul>
        {(data?.tag_history ?? []).some((row) => row.removed_at) ? (
          <details className="text-sm">
            <summary className="cursor-pointer text-ink-400">Tag history</summary>
            <ul className="mt-2 space-y-1 text-ink-600">
              {data!.tag_history.map((row) => (
                <li key={row.assignment_id}>
                  {row.name}: added by {row.assigned_by_name ?? 'staff'} {formatWhen(row.assigned_at)}
                  {row.removed_at ? `, taken off by ${row.removed_by_name ?? 'staff'} ${formatWhen(row.removed_at)}` : ', still on'}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </Panel>
  );
}

/** The managed list of tags: add a kind, retire one, decide which warn at a sale. */
export function TagKindsPanel() {
  const queryClient = useQueryClient();
  const kinds = useQuery({ queryKey: ['customers', 'tag-kinds', 'all'], queryFn: () => customerEndpoints.tagKinds(true) });
  const [name, setName] = useState('');
  const [warns, setWarns] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['customers', 'tag-kinds'] });
  const create = useMutation({
    mutationFn: () => customerEndpoints.createTagKind({ name: name.trim(), warns_at_sale: warns }),
    onSuccess: () => {
      setName('');
      setWarns(false);
      setError(null);
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const update = useMutation({
    mutationFn: (input: { code: string; active?: boolean; warns_at_sale?: boolean }) => customerEndpoints.updateTagKind(input.code, input),
    onSuccess: refresh,
    onError: (cause: ApiError) => setError(cause.message),
  });
  return (
    <Panel title="Customer tags" description="The list staff pick from. A tag that warns at a sale is shown in red to the cashier and on the New Order page; it never stops the sale.">
      {error ? (
        <div className="px-5 pt-3">
          <ErrorNote>{error}</ErrorNote>
        </div>
      ) : null}
      <ul className="divide-y divide-line">
        {(kinds.data?.tags ?? []).map((kind) => (
          <li key={kind.code} className="flex flex-wrap items-center gap-3 px-5 py-2 text-sm">
            <Badge tone={kind.active ? (kind.warns_at_sale ? 'bad' : 'accent') : 'neutral'}>{kind.name}</Badge>
            <label className="flex items-center gap-1 text-ink-600">
              <input type="checkbox" checked={kind.warns_at_sale === 1} onChange={(e) => update.mutate({ code: kind.code, warns_at_sale: e.target.checked })} /> Warns at a sale
            </label>
            <button type="button" className="ml-auto text-xs text-ink-400 underline" onClick={() => update.mutate({ code: kind.code, active: kind.active !== 1 })}>
              {kind.active ? 'Retire' : 'Bring back'}
            </button>
          </li>
        ))}
      </ul>
      <form
        className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New tag, e.g. Season subscriber" className={`${INPUT} w-full max-w-xs`} aria-label="New tag name" />
        <label className="flex items-center gap-1 text-sm text-ink-600">
          <input type="checkbox" checked={warns} onChange={(e) => setWarns(e.target.checked)} /> Warns at a sale
        </label>
        <Button type="submit" disabled={!name.trim() || create.isPending}>
          Add tag
        </Button>
      </form>
    </Panel>
  );
}
